import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  useTheme,
  CircularProgress,
  alpha,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { MonetizationOn as MonetizationOnIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAgentReport } from '../../../../contexts/AgentReportContext';
import { getBookingStatusColor } from '../../../../utils/helper';

const Bookings = ({ agentId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { fetchSingleAgentBookings } = useAgentReport();

  const [bookingsData, setBookingsData] = useState([]);
  const [bookingsList, setBookingsList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState(null);
  const [cardViewTab, setCardViewTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchBookingsData();
  }, [agentId]);

  useEffect(() => {
    fetchBookingsList();
  }, [agentId, currentPage, pageSize]);

  const fetchBookingsData = async () => {
    setLoading(true);
    try {
      const bookingsData = await fetchSingleAgentBookings(agentId, 'overview');
      setBookingsData(bookingsData);
      setError(null);
    } catch (error) {
      setError(error);
      console.error('Error fetching bookings data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingsList = async () => {
    setLoadingList(true);
    try {
      const response = await fetchSingleAgentBookings(
        agentId, 
        'list',
        null, // startDate - null means show all
        null, // endDate - null means show all
        currentPage,
        pageSize
      );
      
      if (response && response.bookings) {
        setBookingsList(response.bookings);
        setPagination(response.pagination);
      } else {
        // Backward compatibility if response is just an array
        setBookingsList(Array.isArray(response) ? response : []);
        setPagination(null);
      }
      setError(null);
    } catch (error) {
      setError(error);
      console.error('Error fetching bookings list:', error);
    } finally {
      setLoadingList(false);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCardViewTab(newValue);
  };

  // Get card data based on selected tab
  const getCardData = () => {
    if (cardViewTab === 0) {
      // Current Month - get latest month data
      const latestMonth = bookingsData && bookingsData.length > 0 ? bookingsData[bookingsData.length - 1] : {};
      return {
          count: latestMonth.count || 0,
          value: latestMonth.value || 0,
          commission: latestMonth.commission || 0
      };
    } else {
      // Lifetime - sum all months
      const totals = bookingsData && bookingsData.length > 0 ? bookingsData.reduce((acc, month) => ({
        count: acc.count + (month.count || 0),
        value: acc.value + (month.value || 0),
        commission: acc.commission + (month.commission || 0)
      }), { count: 0, value: 0, commission: 0 }) : { count: 0, value: 0, commission: 0 };
      return totals;
    }
  };
  
  // Loading state for overview chart
  const isLoadingOverview = loading;
  
  // Error state
  if (error && !bookingsList.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography color="error">Error loading bookings: {error.message || error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Bookings Chart */}
      <Paper sx={{ 
        borderRadius: 3, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
        mb: 3,
        p: 3
      }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Bookings Performance
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Track booking count, value, and commission revenue over the last 12 months
              </Typography>
            </Box>
            
            {/* Performance Cards with Tabs */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <Tabs
                value={cardViewTab}
                onChange={handleTabChange}
                sx={{
                  minHeight: 'auto',
                  '& .MuiTab-root': {
                    minHeight: 'auto',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    minWidth: 'auto'
                  },
                  '& .MuiTabs-indicator': {
                    height: 2,
                    borderRadius: 1
                  }
                }}
              >
                <Tab label="Current Month" />
                <Tab label="Lifetime" />
              </Tabs>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 1.5,
                  p: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  minWidth: 65
                }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                    Count
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                    {getCardData().count}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  borderRadius: 1.5,
                  p: 1,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                  minWidth: 65
                }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                    Value
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                    ₹{getCardData().value}L
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  borderRadius: 1.5,
                  p: 1,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                  minWidth: 65
                }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                    Commission
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                    ₹{getCardData().commission}L
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ height: 300 }}>
          {isLoadingOverview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={bookingsData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.grey[500], 0.3)} />
                <XAxis 
                  dataKey="date" 
                  stroke={theme.palette.text.secondary}
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                />
                <YAxis 
                  yAxisId="left"
                  stroke={theme.palette.text.secondary}
                  fontSize={12}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  stroke={theme.palette.text.secondary}
                  fontSize={12}
                />
                <RechartsTooltip 
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                  formatter={(value, name) => {
                    if (name === 'count') return [value, 'Bookings Count'];
                    if (name === 'value') return [`₹${value}L`, 'Booking Value'];
                    if (name === 'commission') return [`₹${value}L`, 'Commission Revenue'];
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar 
                  yAxisId="left"
                  dataKey="count" 
                  fill="#6366F1"
                  name="Bookings Count"
                  radius={[2, 2, 0, 0]}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="value" 
                  stroke="#10B981"
                  strokeWidth={3}
                  dot={{ fill: "#10B981", strokeWidth: 2, r: 4 }}
                  name="Booking Value"
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="commission" 
                  stroke="#F59E0B"
                  strokeWidth={3}
                  dot={{ fill: "#F59E0B", strokeWidth: 2, r: 4 }}
                  name="Commission Revenue"
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Paper>

      {/* Bookings List */}
      <Paper sx={{ p: 3, boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Bookings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All bookings by this agent
              {pagination && ` (${pagination.total_count} total)`}
            </Typography>
          </Box>
          {pagination && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Per Page</InputLabel>
              <Select
                value={pageSize}
                label="Per Page"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      {loadingList ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : bookingsList && bookingsList.length > 0 ? (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.grey[50], 0.5) }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Booking ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Booking Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Lead</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookingsList.map((booking) => (
                  <TableRow
                    key={booking.id}
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {booking.booking_id || `#${booking.id}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {booking.booking_date ? (
                        <Typography variant="body2" color="text.secondary">
                          {booking.booking_date}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {booking.lead ? (
                        <Typography 
                          variant="body2" 
                          color="primary" 
                          sx={{ 
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/leads/${booking.lead.id}`);
                          }}
                        >
                          {booking.lead.name}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {booking.project ? (
                        <Typography variant="body2" color="text.secondary">
                          {booking.project.name}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {booking.amount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={booking.status_verbose}
                        size="small"
                        sx={{
                          backgroundColor: getBookingStatusColor(booking.status).background || alpha(theme.palette.primary.main, 0.1),
                          color: getBookingStatusColor(booking.status).color || theme.palette.primary.main,
                          borderColor: getBookingStatusColor(booking.status).borderColor || theme.palette.primary.main,
                          border: '1px solid',
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 2 }}>
              <Pagination
                count={pagination.total_pages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
              {pagination.total_count > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  Showing {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, pagination.total_count)} of {pagination.total_count}
                </Typography>
              )}
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          py: 8,
          textAlign: 'center'
        }}>
          <Box
            sx={{
              p: 3,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.grey[300], 0.3),
              color: theme.palette.grey[500],
              mb: 2,
            }}
          >
            <MonetizationOnIcon sx={{ fontSize: 48 }} />
          </Box>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No Bookings Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This agent hasn't made any bookings yet.
          </Typography>
        </Box>
      )}
      </Paper>
    </Box>
  );
};

export default Bookings;
